import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

/** Actions the published-dashboard settings menu can fire from the shared top
 *  toolbar. The published dashboard component listens and performs each one. */
export type PublishedDashboardAction = 'edit' | 'reset' | 'cancel' | 'save' | 'back' | 'share' | 'duplicate';

/** What the top toolbar needs to render the published-dashboard controls:
 *  whether a published dashboard is currently on screen at all (`active`),
 *  whether it's in layout-edit mode, and which actions apply. */
export interface PublishedDashboardToolbarState {
  active: boolean;
  editMode: boolean;
  hasWidgets: boolean;
  showOwnerActions: boolean;
}

const INACTIVE: PublishedDashboardToolbarState = {
  active: false,
  editMode: false,
  hasWidgets: false,
  showOwnerActions: false,
};

/**
 * Bridges the published dashboard's toolbar controls (Edit Layout / Reset /
 * Back to Builder / Share / Cancel / Save) up to the shared top navbar, so the
 * settings gear lives in the top bar instead of a floating in-page strip.
 *
 * Mirrors DashboardTitleService's simple BehaviorSubject pattern: the published
 * dashboard PUSHES its state (`setState`) and SUBSCRIBES to `action$`; the
 * side-navbar SUBSCRIBES to `state$` (to render the menu + its enabled/edit
 * state) and calls `trigger(...)` when a menu item is clicked.
 */
@Injectable({ providedIn: 'root' })
export class PublishedDashboardToolbarService {
  private readonly stateSubject = new BehaviorSubject<PublishedDashboardToolbarState>(INACTIVE);
  readonly state$ = this.stateSubject.asObservable();

  private readonly actionSubject = new Subject<PublishedDashboardAction>();
  readonly action$ = this.actionSubject.asObservable();

  setState(state: Partial<PublishedDashboardToolbarState>): void {
    this.emit({ ...this.stateSubject.value, ...state, active: true });
  }

  clear(): void {
    this.emit(INACTIVE);
  }

  /**
   * The publisher is a router-outlet CHILD of the navbar that renders this, so
   * a synchronous push lands after the navbar was checked → NG0100. The
   * microtask defers it past `tick()`; FIFO keeps `clear()` before the next
   * `setState()` on navigation. The equality guard drops no-op re-pushes.
   */
  private emit(next: PublishedDashboardToolbarState): void {
    if (this.isSameState(this.pending ?? this.stateSubject.value, next)) {
      return;
    }
    this.pending = next;
    void Promise.resolve().then(() => {
      if (!this.pending || !this.isSameState(this.pending, next)) return;
      this.pending = null;
      this.stateSubject.next(next);
    });
  }

  private pending: PublishedDashboardToolbarState | null = null;

  private isSameState(a: PublishedDashboardToolbarState, b: PublishedDashboardToolbarState): boolean {
    return a.active === b.active
      && a.editMode === b.editMode
      && a.hasWidgets === b.hasWidgets
      && a.showOwnerActions === b.showOwnerActions;
  }

  trigger(action: PublishedDashboardAction): void {
    this.actionSubject.next(action);
  }
}
